import { __decorate, __metadata, __param } from "tslib";
// ref: https://confluence.zhihuiya.com/display/~zhanggang/TOC-35029+-+Package+Management+APIs
import _ from 'lodash';
import qs from 'qs';
import { inject, injectable } from 'inversify';
import { format } from '@/@core/utils/string';
import { HTTP_BATCH_TIMEOUT } from './variable';
import { Symbols } from '../../symbols';
import { maybeFillArrayFiled } from '../../utils/lang';
/**
 * 套餐搜索时，如果选中付费类型，使用用户选中的付费类型
 * 如果付费类型没有选项，默认排除内部选项，剩余的选项全部选中
 **/
const packageDefaultChargeModes = ['COMMERCIAL', 'TRIAL', 'FREE', 'HISTORICAL', 'BETA', 'COOPERATIVE', 'FREEMIUM'];
const packageBusinessModeOrder = ['credits', 'seats'];
function formatBusinessModes(businessModes = []) {
    return _.join(_.sortBy(_.uniq(_.map(businessModes, item => _.toLower(item))), item => {
        const index = _.indexOf(packageBusinessModeOrder, item);
        return index > -1 ? index : packageBusinessModeOrder.length;
    }));
}
function formatBusinessModeQuery(businessMode) {
    if (_.isArray(businessMode)) {
        return _.join(businessMode, ',');
    }
    return businessMode || '';
}
let PackageApi = class PackageApi {
    constructor(http, urls, miscApi, boHelperService) {
        this.http = http;
        this.urls = urls;
        this.miscApi = miscApi;
        this.boHelperService = boHelperService;
        // 通过收费模式取package列表
        this.getPackageByChargeMode = (mode) => {
            const url = this.urls.getPackageByChargeMode;
            const fullUrl = format(url, { mode });
            return this.http.get({
                url: fullUrl,
            });
        };
        // 获取company的 privilege列表
        this.getCompanyPrivilege = (company_id, params) => {
            const url = this.urls.getCompanyPrivilege;
            const fullUrl = format(url, {
                company_id,
            });
            return this.http.get({
                url: fullUrl,
                params,
            });
        };
        // 获取company的 Credits权限列表
        this.getCompanyCreditsPrivileges = (company_id) => {
            const url = this.urls.getCompanyCreditsPrivileges;
            const fullUrl = format(url, {
                company_id,
            });
            return this.http.get({
                url: fullUrl,
            });
        };
        // 新增company的 Credits权限
        this.postAddCreditsPrivilege = (params) => {
            const url = this.urls.postAddCreditsPrivilege;
            return this.http.post({
                url,
                data: params,
            });
        };
        // 编辑company的 Credits权限
        this.putUpdateCreditsPrivilege = (params) => {
            const url = this.urls.putUpdateCreditsPrivilege;
            return this.http.put({
                url,
                data: params,
            });
        };
        // 启用/禁用company的 Credits权限
        this.putCreditsPrivilegeEnabled = (params) => {
            const url = this.urls.putCreditsPrivilegeEnabled;
            return this.http.put({
                url,
                data: params,
            });
        };
        // 更新company privilege Credits功能开关
        this.putCompanyPrivilegeExcludeFeatures = (params) => {
            const url = this.urls.putCompanyPrivilegeExcludeFeatures;
            return this.http.put({
                url,
                data: params,
            });
        };
        // 批量分配Credits权限
        this.postBatchAssignCredits = (params) => {
            const url = this.urls.postBatchAssignCredits;
            return this.http.post({
                url,
                data: params,
            });
        };
        // 获取公司Credits配置
        this.getCompanyCreditsConfig = (company_id) => {
            const url = format(this.urls.getCompanyCreditsConfig, { company_id });
            return this.http.get({ url });
        };
        // 更新公司Credits配置
        this.putCompanyCreditsConfig = (params) => {
            return this.http.put({
                url: this.urls.putCompanyCreditsConfig,
                data: params,
            });
        };
        // 修改公司和公司privilege
        this.patchModifyCompanyPrivilege = (params) => {
            const url = this.urls.patchModifyCompanyPrivilege;
            return this.http.patch({
                url,
                data: params,
            });
        };
        // 批量修改用户信息
        // warn: 分配失败的用户放在了, 返回结果里面
        this.postBatchAssignUsers = (params) => {
            params = this.boHelperService.maybeSerializeBeginDate(params, 'begin_at_value');
            params = this.boHelperService.maybeSerializeEndDate(params, 'end_at_value');
            const url = this.urls.postBatchAssignUsers;
            return this.http.post({
                url,
                data: params,
                timeout: HTTP_BATCH_TIMEOUT,
            });
        };
    }
    // 获取分页列表
    getListPackages(params) {
        const url = this.urls.getListPackage;
        const paramsSend = {
            ...params,
            s_charge_modes: params.s_charge_mode ? params.s_charge_mode : _.join(packageDefaultChargeModes, ','),
            s_business_mode: formatBusinessModeQuery(params.s_business_mode),
            s_product_role_id: _.join(params.s_product_role_id, ','),
            s_product_id: _.join(params.s_product_id, ','),
            s_package_ids: _.join(params.s_package_ids, ','),
        };
        window.rvTrack('Search Package List', paramsSend);
        return this.http.get({
            url,
            params: paramsSend,
        });
    }
    // 获取套餐信息
    getPackage(package_id) {
        const url = format(this.urls.getPackage, {
            package_id,
        });
        return this.http.get({
            url,
        });
    }
    // 创建套餐
    postCreatePackage(params) {
        const url = this.urls.postCreatePackage;
        const { productRoleIds, chargeModes, businessModes, selectedProductIds, kickProductIds, } = params;
        const paramsSend = {
            ..._.omit(params, 'businessModes'),
            productRoleIds: _.join(productRoleIds),
            chargeModes: _.join(chargeModes),
            businessMode: formatBusinessModes(businessModes),
            selectedProductIds: _.join(selectedProductIds, ','),
            kickProductIds: _.join(kickProductIds, ','),
        };
        const formData = qs.stringify(paramsSend);
        return this.http.post({
            url,
            data: formData,
        });
    }
    // 更新套餐
    patchUpdatePackage(params) {
        const url = this.urls.patchUpdatePackage;
        const { productRoleIds, chargeModes, businessModes, selectedProductIds, kickProductIds, } = params;
        const paramsSend = _.pickBy(_.omit(params, 'businessModes'), x => !_.isNil(x));
        maybeFillArrayFiled(productRoleIds, value => { paramsSend.productRoleIds = value; });
        maybeFillArrayFiled(chargeModes, value => { paramsSend.chargeModes = value; });
        if (_.isArray(businessModes)) {
            paramsSend.businessMode = formatBusinessModes(businessModes);
        }
        maybeFillArrayFiled(selectedProductIds, value => { paramsSend.selectedProductIds = value; });
        maybeFillArrayFiled(kickProductIds, value => { paramsSend.kickProductIds = value; });
        const formData = qs.stringify(paramsSend);
        return this.http.patch({
            url,
            data: formData,
        });
    }
    // 添加privilege
    // warn: 分配失败的用户放在了, 返回结果里面
    postAddPrivilege(params) {
        const url = this.urls.postAddPrivilege;
        return this.http.post({
            url,
            data: params,
        });
    }
    // 修改company-privilege
    // warn: 分配失败的用户放在了, 返回结果里面
    putUpdateCompanyPrivilege(params) {
        const url = this.urls.putUpdateCompanyPrivilege;
        return this.http.put({
            url,
            data: params,
        });
    }
    // 分配用户
    // warn: 分配失败的用户放在了, 返回结果里面
    postCompanyPrivilegeAssignation(params) {
        const url = this.urls.postCompanyPrivilegeAssignation;
        // 超时2分钟
        const timeout = 1000 * 60 * 2;
        return this.http.post({
            url,
            data: params,
            timeout,
        });
    }
    // 启用合同
    patchEnablePrivilege(privilege_id) {
        const url = format(this.urls.patchEnablePrivilege, {
            privilege_id,
        });
        return this.http.patch({
            url,
        });
    }
    // 禁止合同
    patchDisablePrivilege(privilege_id) {
        const url = format(this.urls.patchDisablePrivilege, {
            privilege_id,
        });
        return this.http.patch({
            url,
        });
    }
    // 获取所有的package
    getPackageInfoList() {
        const url = this.urls.getPackageInfoList;
        return this.http.get({
            url,
        });
    }
    // 获取套餐容量配置
    getPackageCapacityConfig(packageId) {
        const url = format(this.urls.getPackageCapacityConfig, { package_id: packageId });
        return this.http.get({
            url,
        });
    }
    /**
     * Misc
    */
    getChargeModes() {
        return this.miscApi.getSystemCodes('CHARGE_MODE');
    }
    getPackagePromotion() {
        const url = this.urls.getPackagePromotion;
        return this.http.get({
            url,
        });
    }
};
PackageApi = __decorate([
    injectable(),
    __param(0, inject(Symbols.BoHttp)),
    __param(1, inject(Symbols.PackageApiUrls)),
    __param(2, inject(Symbols.MiscApi)),
    __param(3, inject(Symbols.BoHelperService)),
    __metadata("design:paramtypes", [Object, Object, Object, Object])
], PackageApi);
export { PackageApi };
